import { useState, useEffect, useCallback, useRef } from "react";

const VISIT_COUNT_KEY = "isaiah_visit_count";
const DISMISSED_KEY = "isaiah_install_dismissed";
const NEVER_KEY = "isaiah_install_never"; // permanent opt-out ("Don't remind me again")
const SHOWN_KEY = "isaiah_install_last_shown"; // last time the banner was actually shown
const DISMISSED_DURATION = 14 * 24 * 60 * 60 * 1000; // 14 days snooze
const SHOW_INTERVAL = 7 * 24 * 60 * 60 * 1000; // show at most once a week
const MIN_VISITS = 1;

/**
 * Captures the browser's `beforeinstallprompt` event and manages
 * display logic for the install banner.
 *
 * Returns:
 *   canShow      , true when the banner should be rendered
 *   triggerInstall, call this to open the native install dialog
 *   dismiss       , call this to hide the banner for 14 days
 *   isInstalled   , true if the app is already installed (standalone mode)
 */
export function useInstallPrompt() {
  const deferredPrompt = useRef(null);
  const [canShow, setCanShow] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already running as installed PWA
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setIsInstalled(true);
      return;
    }

    // Permanent opt-out, never show again
    if (localStorage.getItem(NEVER_KEY) === "1") return;

    // Increment visit count
    const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10) + 1;
    localStorage.setItem(VISIT_COUNT_KEY, String(count));

    // Check if snoozed recently (14-day window)
    const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISSED_DURATION) return;

    // Rate-limit: only surface the banner once a week, even if the visitor
    // never tapped "Not now" — nobody wants an install nag on every login.
    const lastShownAt = parseInt(localStorage.getItem(SHOWN_KEY) || "0", 10);
    if (lastShownAt && Date.now() - lastShownAt < SHOW_INTERVAL) return;

    // Only show after enough visits
    if (count < MIN_VISITS) return;

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      // Stamp "shown now" so it won't reappear again for a week.
      try { localStorage.setItem(SHOWN_KEY, String(Date.now())); } catch { /* ignore */ }
      setCanShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Also listen for successful install
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setCanShow(false);
      deferredPrompt.current = null;
    });

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt.current) return false;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    setCanShow(false);
    return outcome === "accepted";
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setCanShow(false);
  }, []);

  // Permanent opt-out, the user chose "Don't remind me again".
  const dismissForever = useCallback(() => {
    try { localStorage.setItem(NEVER_KEY, "1"); } catch { /* ignore */ }
    setCanShow(false);
  }, []);

  return { canShow, triggerInstall, dismiss, dismissForever, isInstalled };
}
